import type { Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';

/**
 * Shared card surface used by the contact info and preferences sections.
 */
export const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
  p: 2,
};

/**
 * Page container background for all Account Hub screens.
 * Light mode: grey[200] (#F4F6F8)
 * Dark mode:  background.default (the theme's dark background)
 */
export const hubPageBg = (theme: Theme): string =>
  theme.palette.mode === 'dark'
    ? theme.palette.background.default
    : theme.palette.grey[200];

/**
 * Interactive link/button text color.
 * Light: secondary.main (#3366FF) — 4.69:1 on white ✅
 * Dark:  secondary.light (#84A9FF) — light blue, readable on dark surfaces ✅
 */
export const hubLinkColor = (theme: Theme): string =>
  theme.palette.mode === 'dark'
    ? theme.palette.secondary.light
    : theme.palette.secondary.main;

/**
 * Ghost button — matches the Change/Edit buttons across Account Hub.
 * Light: grey.200 background, secondary.dark text, 8px radius.
 * Dark:  action.selected background (semi-transparent tint), secondary.light text.
 */
export const hubGhostBtnSx = (theme: Theme): Record<string, unknown> => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.action.selected
      : theme.palette.grey[200],
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.dark,
  fontWeight: 500,
  fontSize: 14,
  borderRadius: '8px',
  px: 2,
  py: 1,
  minWidth: 0,
  boxShadow: 'none',
  textTransform: 'none' as const,
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.action.hover
        : theme.palette.grey[300],
    boxShadow: 'none',
  },
});

/**
 * Preferences toggle switch matching the mockup:
 * Enabled:  secondary.main track (#3366FF), white thumb — 44×24px
 * Disabled: grey.500 track (#919EAB), white thumb
 */
export const HubSwitch = styled(Switch)(({ theme }) => ({
  width: 44,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: theme.palette.background.paper,
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.secondary.main,
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: 12,
    backgroundColor: theme.palette.grey[500],
    opacity: 1,
  },
}));
